import React from "react";
import { MdMoreVert } from "react-icons/md";
import ContextMenu from "components/ui/ContextMenu";
import { rowActionChipClass, ROW_ACTIONS_TRIGGER, type RowActionVariant } from "lib/rowActionStyles";

export type DataTableRowAction = {
  id: string;
  label: string;
  onClick: () => void;
  variant?: RowActionVariant;
  disabled?: boolean;
  hidden?: boolean;
};

type DataTableRowActionsProps = {
  actions: DataTableRowAction[];
  isArabic: boolean;
  /** Show inline chips when visible actions count is at or below this value. */
  inlineThreshold?: number;
};

function menuItemClass(variant: RowActionVariant = "default", danger?: boolean): string {
  if (danger || variant === "danger") {
    return "text-red-600 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-500/10";
  }
  if (variant === "success") {
    return "text-emerald-700 hover:bg-emerald-50 dark:text-emerald-300 dark:hover:bg-emerald-500/10";
  }
  if (variant === "warning") {
    return "text-amber-700 hover:bg-amber-50 dark:text-amber-300 dark:hover:bg-amber-500/10";
  }
  if (variant === "purple") {
    return "text-purple-700 hover:bg-purple-50 dark:text-purple-300 dark:hover:bg-purple-500/10";
  }
  if (variant === "primary") {
    return "text-brand-600 hover:bg-brand-50 dark:text-brand-300 dark:hover:bg-brand-500/10";
  }
  return "text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/10";
}

/**
 * Row actions: compact chips for few actions, dropdown menu for many.
 */
export default function DataTableRowActions({
  actions,
  isArabic,
  inlineThreshold = 2,
}: DataTableRowActionsProps) {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const visible = actions.filter((a) => !a.hidden);

  if (visible.length === 0) return null;

  if (visible.length <= inlineThreshold) {
    return (
      <div
        className={`flex flex-wrap items-center gap-1.5 ${
          isArabic ? "flex-row-reverse justify-end" : "justify-start"
        }`}
      >
        {visible.map((action) => (
          <button
            key={action.id}
            type="button"
            disabled={action.disabled}
            onClick={action.onClick}
            className={rowActionChipClass(action.variant ?? "default")}
          >
            {action.label}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="relative max-w-full">
      <button
        ref={anchorRef}
        type="button"
        onClick={() => setMenuOpen(true)}
        className={`${ROW_ACTIONS_TRIGGER} ${isArabic ? "flex-row-reverse" : ""}`}
        aria-haspopup="menu"
        aria-expanded={menuOpen}
      >
        <MdMoreVert className="h-4 w-4 shrink-0" />
        <span className="truncate">{isArabic ? "إجراءات" : "Actions"}</span>
      </button>
      <ContextMenu
        anchor={anchorRef.current}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        isArabic={isArabic}
        items={visible.map((action) => ({
          id: action.id,
          label: action.label,
          disabled: action.disabled,
          danger: action.variant === "danger",
          itemClassName: menuItemClass(action.variant, action.variant === "danger"),
          onSelect: action.onClick,
        }))}
      />
    </div>
  );
}
